import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { 
  Truck, 
  Tag, 
  Flame, 
  Sparkles, 
  Zap, 
  Gift, 
  Bell, 
  Pause, 
  Play, 
  ChevronRight,
  Copy,
  Check
} from 'lucide-react';
import { NewsTickerItem } from '../types';

export const NewsTicker: React.FC = () => {
  const { newsTicker, setCurrentView, setSelectedProduct, showToast, language } = useStore();
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  if (!newsTicker || !newsTicker.enabled) {
    return null;
  }

  const activeItems = (newsTicker.items || []).filter((item) => item.active);
  if (activeItems.length === 0) {
    return null;
  }

  // Multiply items for smooth infinite loop regardless of widescreen resolution
  const tickerItems = [...activeItems, ...activeItems, ...activeItems, ...activeItems];

  const renderIcon = (iconName?: string) => {
    switch (iconName) {
      case 'truck':
        return <Truck className="w-3.5 h-3.5 shrink-0" />;
      case 'tag':
        return <Tag className="w-3.5 h-3.5 shrink-0" />;
      case 'flame':
        return <Flame className="w-3.5 h-3.5 shrink-0" />;
      case 'zap':
        return <Zap className="w-3.5 h-3.5 shrink-0" />;
      case 'gift':
        return <Gift className="w-3.5 h-3.5 shrink-0" />;
      case 'bell':
        return <Bell className="w-3.5 h-3.5 shrink-0" />;
      case 'sparkles':
      default:
        return <Sparkles className="w-3.5 h-3.5 shrink-0" />;
    }
  };

  const handleItemClick = (item: NewsTickerItem) => {
    // If text contains a promo code like COVERISTIC10, copy to clipboard
    const promoMatch = item.text.match(/\b([A-Z0-9]{5,15})\b/);
    if (item.text.toLowerCase().includes('code') && promoMatch) {
      const code = promoMatch[0];
      navigator.clipboard.writeText(code);
      setCopiedId(item.id);
      showToast(language === 'ar' ? `تم نسخ رمز الخصم "${code}"!` : `Promo code "${code}" copied to clipboard!`);
      setTimeout(() => setCopiedId(null), 3000);
      return;
    }

    setSelectedProduct(null);
    if (item.link === 'shop') {
      setCurrentView('shop');
    } else if (item.link === 'custom-print') {
      setCurrentView('custom-print');
    } else if (item.link === 'about') {
      setCurrentView('about');
    }
  };

  const speedSeconds = Math.max(8, Math.min(100, newsTicker.speed || 25));

  // Localized text mappings for default items
  const getItemText = (item: NewsTickerItem) => {
    if (language !== 'ar') return item.text;
    if (item.id === 'news-1' || item.text.includes('Free delivery this Sunday')) {
      return 'توصيل مجاني هذا الأحد لكافة طلبيات الطباعة المخصصة في لبنان!';
    }
    if (item.id === 'news-2' || item.text.includes('10% OFF')) {
      return 'خصم 10% على كافة المنتجات — استخدم الرمز COVERISTIC10 عبر واتساب!';
    }
    if (item.id === 'news-3' || item.text.includes('Cash on Delivery')) {
      return 'توصيل سريع لكافة مناطق لبنان والدفع نقداً عند الاستلام بالدولار ($ USD)';
    }
    if (item.id === 'news-4' || item.text.includes('preview')) {
      return 'معاينة وبروفة مجانية متوفرة قبل البدء بالطباعة عبر واتساب (+961 81 773 349)';
    }
    return item.text;
  };

  const getItemBadge = (item: NewsTickerItem) => {
    if (!item.badge) return item.badge;
    if (language !== 'ar') return item.badge;
    if (item.id === 'news-1' || item.badge.includes('FREE')) return 'توصيل مجاني';
    if (item.id === 'news-2' || item.badge.includes('10%')) return 'خصم ١٠٪';
    if (item.id === 'news-3' || item.badge.includes('EXPRESS')) return 'توصيل سريع';
    if (item.id === 'news-4' || item.badge.includes('PREVIEW')) return 'بروفة مجانية';
    return item.badge;
  };

  return (
    <div
      id="home-news-ticker"
      className="news-ticker-container relative w-full border-y overflow-hidden z-20 select-none transition-colors duration-300"
      style={{
        backgroundColor: newsTicker.backgroundColor || '#111111',
        color: newsTicker.textColor || '#FFFFFF',
        borderColor: newsTicker.borderColor || '#262626',
        ['--ticker-speed' as string]: `${speedSeconds}s`,
      }}
      role="region"
      aria-label="Latest Announcements and News"
    >
      <div className="max-w-7xl mx-auto flex items-center h-11 sm:h-12 px-2 sm:px-4">
        {/* Left Indicator Pill: "FLASH NEWS" / "LIVE UPDATES" */}
        <div
          className="shrink-0 flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded font-black text-[10px] sm:text-[11px] uppercase tracking-wider shadow-sm z-10 transition-transform active:scale-95"
          style={{
            backgroundColor: newsTicker.badgeBackgroundColor || '#E21B24',
            color: newsTicker.badgeTextColor || '#FFFFFF',
          }}
          title="Live Store Announcements"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
          <span className="hidden xs:inline sm:inline font-extrabold whitespace-nowrap">
            {language === 'ar' ? 'أحدث الأخبار' : (newsTicker.label || 'FLASH NEWS')}
          </span>
          <span className="xs:hidden sm:hidden font-extrabold">{language === 'ar' ? 'مباشر' : 'LIVE'}</span>
        </div>

        {/* Center Infinite Scrolling Marquee Track */}
        <div className="relative flex-1 overflow-hidden h-full flex items-center mx-2 sm:mx-3">
          {/* Subtle gradient edge fades */}
          <div
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 sm:w-10 z-10"
            style={{
              background: `linear-gradient(to right, ${newsTicker.backgroundColor || '#111111'}, transparent)`,
            }}
          />
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 sm:w-10 z-10"
            style={{
              background: `linear-gradient(to left, ${newsTicker.backgroundColor || '#111111'}, transparent)`,
            }}
          />

          <div
            className={`news-ticker-track ${
              newsTicker.pauseOnHover ? 'pause-on-hover' : ''
            } ${isManuallyPaused ? 'is-paused' : ''} flex items-center gap-6 sm:gap-10`}
          >
            {tickerItems.map((item, index) => {
              const hasAction = Boolean(item.link || item.text.toLowerCase().includes('code'));
              const isCopied = copiedId === item.id;

              return (
                <div
                  key={`${item.id}-${index}`}
                  onClick={() => handleItemClick(item)}
                  className={`flex items-center gap-2 sm:gap-2.5 shrink-0 text-xs sm:text-[13px] font-semibold tracking-wide transition-opacity ${
                    hasAction ? 'cursor-pointer hover:opacity-80 active:scale-98' : ''
                  }`}
                  style={{ color: newsTicker.textColor || '#FFFFFF' }}
                  title={hasAction ? 'Click to view offer or copy code' : undefined}
                >
                  {/* Icon */}
                  {newsTicker.showIcon && item.icon && (
                    <span 
                      className="p-1 rounded-full shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: `${newsTicker.badgeBackgroundColor || '#E21B24'}22`,
                        color: newsTicker.badgeBackgroundColor || '#E21B24',
                      }}
                    >
                      {renderIcon(item.icon)}
                    </span>
                  )}

                  {/* Badge */}
                  {newsTicker.showBadge && item.badge && (
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] sm:text-[10px] font-black uppercase tracking-wider shrink-0"
                      style={{
                        backgroundColor: newsTicker.badgeBackgroundColor || '#E21B24',
                        color: newsTicker.badgeTextColor || '#FFFFFF',
                      }}
                    >
                      {getItemBadge(item)}
                    </span>
                  )}

                  {/* Announcement Text */}
                  <span className="whitespace-nowrap font-medium flex items-center gap-1.5">
                    {getItemText(item)}
                    {hasAction && (
                      <span className="inline-flex items-center text-[10px] opacity-75 font-mono ml-0.5">
                        {isCopied ? (
                          <span className="flex items-center gap-0.5 text-emerald-400 font-bold">
                            <Check className="w-3 h-3" /> {language === 'ar' ? 'تم النسخ' : 'Copied'}
                          </span>
                        ) : item.text.toLowerCase().includes('code') ? (
                          <span className="flex items-center gap-0.5 bg-white/10 hover:bg-white/20 px-1 py-0.5 rounded text-[9px]">
                            <Copy className="w-2.5 h-2.5" /> {language === 'ar' ? 'انسخ الكود' : 'Tap to copy'}
                          </span>
                        ) : (
                          <ChevronRight className="w-3 h-3 opacity-60" />
                        )}
                      </span>
                    )}
                  </span>

                  {/* Item Separator Dot */}
                  <span 
                    className="w-1.5 h-1.5 rounded-full shrink-0 mx-2 opacity-40" 
                    style={{ backgroundColor: newsTicker.textColor || '#FFFFFF' }}
                    aria-hidden="true"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Action: Play / Pause Control */}
        <div className="shrink-0 flex items-center pl-1">
          <button
            type="button"
            onClick={() => setIsManuallyPaused(!isManuallyPaused)}
            className="p-1.5 rounded-md hover:bg-white/10 text-neutral-300 hover:text-white transition-colors flex items-center gap-1 text-[11px] font-medium"
            title={isManuallyPaused ? (language === 'ar' ? 'تشغيل شريط الأخبار' : 'Resume news ticker') : (language === 'ar' ? 'إيقاف شريط الأخبار' : 'Pause news ticker')}
            aria-label={isManuallyPaused ? 'Resume news ticker' : 'Pause news ticker'}
          >
            {isManuallyPaused ? (
              <Play className="w-3.5 h-3.5 fill-current" />
            ) : (
              <Pause className="w-3.5 h-3.5 fill-current" />
            )}
            <span className="hidden lg:inline text-[10px] opacity-70">
              {isManuallyPaused ? (language === 'ar' ? 'تشغيل' : 'Play') : (language === 'ar' ? 'إيقاف' : 'Pause')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
